import { useContext, useEffect, useState } from 'react';
import { Button, Col, Empty, List, Row, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { getAllMatricesByProjectId, getMatrix } from '../../../features/projects/matrixSlices';
import { MatrixLikelihoodType, MatrixRiskType, MatrixSeverityType, MatrixType } from '../../../types/types';
import RiskMatrix from './RiskMatrix';
import { NotificationContext } from '../../../providers/NotificationProvider';

type MatrixWithLevels = MatrixType & {
  matrix_severity?: MatrixSeverityType[];
  matrix_likelihood?: MatrixLikelihoodType[];
  matrix_risk?: MatrixRiskType[];
};

type MatrixViewerOverlayProps = {
  projectId?: string;
  open: boolean;
  onClose: () => void;
};

const MatrixViewerOverlay: React.FC<MatrixViewerOverlayProps> = ({ projectId, open, onClose }) => {
  const dispatch = useDispatch();
  const { triggerNotification } = useContext(NotificationContext);

  const [matrices, setMatrices] = useState<MatrixType[]>([]);
  const [activeMatrixId, setActiveMatrixId] = useState<number | null>(null);
  const [selectedMatrix, setSelectedMatrix] = useState<MatrixType | null>(null);
  const [severityLevels, setSeverityLevels] = useState<MatrixSeverityType[]>([]);
  const [likelihoodLevels, setLikelihoodLevels] = useState<MatrixLikelihoodType[]>([]);
  const [riskLevels, setRiskLevels] = useState<MatrixRiskType[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMatrix, setLoadingMatrix] = useState(false);

  const fetchMatrices = (projectIdParam: string) => {
    setLoadingList(true);
    dispatch(getAllMatricesByProjectId(parseInt(projectIdParam, 10)) as any)
      .then((result: any) => {
        if (getAllMatricesByProjectId.fulfilled.match(result)) {
          const fetchedMatrices: MatrixType[] = result.payload ?? [];
          setMatrices(fetchedMatrices);
          if (fetchedMatrices.length > 0) {
            handleSelectMatrix(fetchedMatrices[0].id ?? null);
          } else {
            clearMatrixSelection();
          }
        } else {
          triggerNotification('error', 'Error', 'No se pudieron cargar las matrices', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudieron cargar las matrices', 'bottomRight');
      })
      .finally(() => {
        setLoadingList(false);
      });
  };

  useEffect(() => {
    if (!open || !projectId) {
      return;
    }
    fetchMatrices(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const clearMatrixSelection = () => {
    setActiveMatrixId(null);
    setSelectedMatrix(null);
    setSeverityLevels([]);
    setLikelihoodLevels([]);
    setRiskLevels([]);
  };

  const handleSelectMatrix = (matrixId: number | null) => {
    if (!projectId || !matrixId) {
      setActiveMatrixId(matrixId ?? null);
      return;
    }
    setActiveMatrixId(matrixId);
    loadMatrixDetails(projectId, matrixId);
  };

  const loadMatrixDetails = (projectIdParam: string, matrixId: number) => {
    setLoadingMatrix(true);
    dispatch(
      getMatrix({
        project_id: parseInt(projectIdParam, 10),
        matrix_id: matrixId,
      }) as any
    )
      .then((result: any) => {
        if (getMatrix.fulfilled.match(result)) {
          const matrixData = result.payload as MatrixWithLevels;
          setSelectedMatrix(matrixData);
          setSeverityLevels(matrixData.matrix_severity ?? []);
          setLikelihoodLevels(matrixData.matrix_likelihood ?? []);
          setRiskLevels(matrixData.matrix_risk ?? []);
        } else {
          triggerNotification('error', 'Error', 'No se pudo cargar la matriz seleccionada', 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', 'Error', 'No se pudo cargar la matriz seleccionada', 'bottomRight');
      })
      .finally(() => {
        setLoadingMatrix(false);
      });
  };

  if (!open) {
    return null;
  }

  return (
    <div className='matrix-overlay'>
      <div className='matrix-overlay-body'>
        <div className='matrix-overlay-header'>
          <Button icon={<CloseOutlined />} onClick={onClose} type='text' aria-label='Cerrar matrices' />
        </div>
        <div className='matrix-overlay-content'>
          {!projectId ? (
            <Empty description='Selecciona un proyecto válido para ver sus matrices' />
          ) : (
            <Row gutter={[24, 24]} style={{ height: '100%' }}>
              <Col xs={24} lg={6} className='matrix-overlay-panel'>
                <div className='matrix-overlay-list'>
                  <List
                    dataSource={matrices}
                    loading={loadingList}
                    locale={{ emptyText: 'No hay matrices registradas' }}
                    renderItem={(item) => (
                      <List.Item
                        key={item.id}
                        className={`matrix-overlay-list-item ${item.id === activeMatrixId ? 'active' : ''}`}
                        onClick={() => handleSelectMatrix(item.id ?? null)}
                      >
                        <List.Item.Meta title={item.name} />
                      </List.Item>
                    )}
                  />
                </div>
              </Col>
              <Col xs={24} lg={18} className='matrix-overlay-panel matrix-overlay-preview'>
                {loadingMatrix ? (
                  <div className='matrix-overlay-preview-empty'>
                    <Spin tip='Cargando matriz...' />
                  </div>
                ) : selectedMatrix ? (
                  <div className='matrix-overlay-preview-body'>
                    <h4>{selectedMatrix.name}</h4>
                    <div className='matrix-overlay-preview-table'>
                      <RiskMatrix
                        severityLevels={severityLevels}
                        likelihoodLevels={likelihoodLevels}
                        riskLevels={riskLevels}
                        inverse={Boolean(selectedMatrix.inverse)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className='matrix-overlay-preview-empty'>
                    <Empty description='Selecciona una matriz para previsualizarla' />
                  </div>
                )}
              </Col>
            </Row>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatrixViewerOverlay;
